/* fourpaw-standard: lifecycle-delegated */
(() => {
  if (window.SafeWalkV6CartSavingsTruth) return;

  const KACHING = '__kaching_bundles';
  const GIFT_ROLES = new Set(['gift_tags', 'gift_dispenser', 'gift_roll']);
  const CART_READ_TIMEOUT_MS = 5000;
  const CART_CACHE_MS = 750;
  const numberPattern = /\d[\d,.]*/;
  let cartRequest = null;
  let cartRevision = 0;
  let cachedCart = null;
  let cachedAt = 0;
  let acceptedCartUntil = 0;
  let timer = 0;
  let running = false;
  let rerun = false;
  let reconciliationTimer = 0;
  let reconciliationRunning = false;
  let reconciliationQueued = false;

  const copy = () => window.SafeWalkCartUiCopy || {};
  const registry = () => window.SafeWalkV6RealGiftRegistry
    || window.SafeWalkCartUiRegistry
    || {};
  const propertiesFor = (item) => item?.properties && typeof item.properties === 'object'
    ? item.properties
    : {};
  const metadataFor = (item) => {
    const raw = propertiesFor(item)[KACHING];
    if (!raw) return null;
    if (typeof raw === 'object') return raw;
    const candidates = [String(raw)];
    try {
      if (typeof window.atob === 'function') candidates.push(window.atob(String(raw)));
    } catch (error) { /* not base64; use the original value */ }
    for (const candidate of candidates) {
      try {
        const parsed = JSON.parse(candidate);
        if (parsed && typeof parsed === 'object') return parsed;
      } catch (error) { /* try the next supported Kaching encoding */ }
    }
    return null;
  };
  const lineTotal = (item) => Number.isFinite(Number(item?.final_line_price))
    ? Number(item.final_line_price)
    : Number(item?.final_price || 0) * Number(item?.quantity || 1);
  const originalTotal = (item) => Number.isFinite(Number(item?.original_line_price))
    ? Number(item.original_line_price)
    : Number(item?.original_price || 0) * Number(item?.quantity || 1);
  const productId = (item) => Number(item?.product_id || item?.product?.id || 0);
  const variantId = (item) => Number(item?.variant_id || item?.id || 0);
  const parentKey = (item) => String(
    item?.parent_relationship?.parent_key
      || item?.parent_relationship?.parent?.key
      || ''
  );
  const centsFromText = (text = '') => {
    const raw = String(text).match(numberPattern)?.[0] || '';
    if (!raw) return null;
    const decimal = raw.match(/([.,])(\d{2})$/);
    const decimalMark = decimal?.[1] || '';
    const normalized = decimalMark
      ? raw.replace(new RegExp(`\\${decimalMark}(?=[^${decimalMark}]*$)`), '|')
      : raw;
    const numeric = Number(normalized.replace(/[.,]/g, '').replace('|', '.'));
    return Number.isFinite(numeric) ? Math.round(numeric * 100) : null;
  };
  const formattedLike = (template, cents) => {
    const raw = String(template).match(numberPattern)?.[0] || '';
    if (!raw) return '';
    const decimal = raw.match(/([.,])(\d{2})$/);
    const decimalMark = decimal?.[1] || '.';
    const fractionDigits = decimal ? 2 : cents % 100 === 0 ? 0 : 2;
    const wholeSample = raw.slice(0, decimal ? -3 : undefined);
    const groupMark = wholeSample.includes(',') ? ',' : wholeSample.includes('.') ? '.' : '';
    const parts = (Math.max(0, cents) / 100).toFixed(fractionDigits).split('.');
    let whole = parts[0];
    if (groupMark) whole = whole.replace(/\B(?=(\d{3})+(?!\d))/g, groupMark);
    return fractionDigits ? `${whole}${decimalMark}${parts[1]}` : whole;
  };
  const mainKind = (metadata, config, legacy = false) => {
    if (!metadata || metadata.main !== true) return '';
    const source = legacy ? config.legacy || {} : config;
    if (!source.deal || metadata.deal !== source.deal) return '';
    if (metadata.bid === source.bars?.harness_only) return 'harness_only';
    if (metadata.bar === source.bars?.harness_bungee) return 'harness_bungee';
    if (metadata.bar === source.bars?.full_set) return 'full_set';
    return '';
  };
  const validMain = (item, config, legacy = false) => Boolean(item)
    && productId(item) === Number(config.qualifyingProductId)
    && Number(item.quantity) === 1
    && lineTotal(item) > 0
    && Boolean(mainKind(metadataFor(item), config, legacy));
  const groupMain = (cart, item, config, legacy = false) => {
    const metadata = metadataFor(item);
    if (!metadata?.id) return null;
    return (cart?.items || []).find((candidate) => {
      const candidateMetadata = metadataFor(candidate);
      return candidateMetadata?.id === metadata.id
        && candidateMetadata?.deal === metadata.deal
        && validMain(candidate, config, legacy);
    }) || null;
  };
  const identityRole = (item, config, requestedRole = '') => {
    const identity = (config.identities || []).find((candidate) => (
      (!requestedRole || candidate.role === requestedRole)
      && productId(item) === Number(candidate.productId)
      && variantId(item) === Number(candidate.variantId)
    ));
    return identity?.role || '';
  };
  const linkedToMain = (item, main) => {
    const key = parentKey(item);
    if (key) return key === String(main?.key || '');
    if (Number(item?.parent_id || 0)) return Number(item.parent_id) === variantId(main);
    return String(metadataFor(item)?.id || '') === String(metadataFor(main)?.id || '');
  };
  const validatedGiftLine = (cart, item, node, config) => {
    if (!item || lineTotal(item) !== 0 || originalTotal(item) <= 0) return false;
    const requestedRole = String(node?.dataset?.safewalkGiftRole || '');
    if (requestedRole && !GIFT_ROLES.has(requestedRole)) return false;
    const role = identityRole(item, config, requestedRole);
    const metadata = metadataFor(item);
    if (!role || !metadata?.id || metadata.main === true) return false;
    const currentMain = groupMain(cart, item, config, false);
    if (currentMain) {
      const kind = mainKind(metadataFor(currentMain), config, false);
      return metadata.gift === config.realGiftIds?.[kind]?.[role]
        && linkedToMain(item, currentMain);
    }
    const legacyMain = groupMain(cart, item, config, true);
    if (!legacyMain || role !== 'gift_dispenser') return false;
    const kind = mainKind(metadataFor(legacyMain), config, true);
    return metadata.gift === config.legacy?.giftIds?.[kind]
      && linkedToMain(item, legacyMain);
  };
  const giftLineNodes = (drawer) => {
    const result = new Map();
    drawer.querySelectorAll(
      '[data-safewalk-gift-surface="drawer"][data-safewalk-savings-line-index], '
      + '[data-safewalk-gift-surface="drawer"] [data-safewalk-cart-line-index]'
    ).forEach((node) => {
      const index = String(
        node.dataset.safewalkSavingsLineIndex || node.dataset.safewalkCartLineIndex || ''
      );
      if (/^\d+$/.test(index) && !result.has(index)) result.set(index, node);
    });
    return result;
  };
  const drawerAdjustment = (drawer, cart, config = registry()) => (
    [...giftLineNodes(drawer)].reduce((sum, [index, node]) => {
      const item = cart?.items?.[Number(index) - 1];
      if (!validatedGiftLine(cart, item, node, config)) return sum;
      const row = drawer.querySelector(`#CartDrawer-Item-${index}`);
      const compare = centsFromText(row?.querySelector('.compare-price')?.textContent || '');
      if (compare == null) return sum;
      return sum + Math.max(0, compare - originalTotal(item));
    }, 0)
  );
  const baseText = (node) => {
    if (!node.dataset.safewalkSavingsBaseText) {
      node.dataset.safewalkSavingsBaseText = node.textContent || '';
    }
    return node.dataset.safewalkSavingsBaseText;
  };
  const adjustNode = (node, adjustment) => {
    if (!node) return;
    const source = baseText(node);
    const original = centsFromText(source);
    if (original == null) return;
    const replacement = formattedLike(source, original - adjustment);
    const nextText = source.replace(numberPattern, replacement);
    if (node.textContent !== nextText) node.textContent = nextText;
  };
  const setText = (node, value) => {
    if (!node || typeof value !== 'string' || !value) return;
    if (node.textContent !== value) node.textContent = value;
  };
  const isFullSet = (cart, config = registry()) => (cart?.items || []).some((item) => (
    validMain(item, config, false) && mainKind(metadataFor(item), config, false) === 'full_set'
  ));
  const syncShipping = (drawer, cart) => {
    const home = isFullSet(cart);
    const values = copy();
    const prefix = home ? 'home' : 'pickup';
    const benefit = drawer.querySelector('.fp-cart-reference-benefit');
    setText(benefit?.querySelector('.fp-cart-reference-benefit__copy strong'), values[`${prefix}Title`]);
    setText(benefit?.querySelector('.fp-cart-reference-benefit__copy small'), values[`${prefix}Detail`]);
    // The controlled reassurance surface owns its copy from the merchant-facing
    // cart guarantee section. Do not overwrite it with legacy pickup/home text.
    setText(drawer.querySelector('.fp-cart-reference-summary__label small'), values[`${prefix}Summary`]);
    const progress = drawer.querySelector('.cart-progress.cart-checkpoints .cart-progress__text');
    if (progress) {
      const progressCopy = values[`${prefix}Progress`] || '';
      progress.dataset.fpShippingCopy = progressCopy;
      if (progressCopy) progress.setAttribute('aria-label', progressCopy);
    }
  };
  const syncSavings = (drawer, cart) => {
    const adjustment = drawerAdjustment(drawer, cart);
    const savings = drawer.querySelector('.fp-cart-savings-row strong');
    const originalSavings = savings ? centsFromText(baseText(savings)) : null;
    adjustNode(savings, adjustment);
    adjustNode(drawer.querySelector('.fp-cart-reference-summary__price s'), adjustment);
    adjustNode(drawer.querySelector('.cart-drawer__total-savings'), adjustment);
    if (originalSavings == null) return;
    drawer.querySelectorAll('.cart-drawer__totals__row__money').forEach((node) => {
      if (centsFromText(baseText(node)) === originalSavings) adjustNode(node, adjustment);
    });
  };
  const syncFocus = () => {
    const open = Boolean(document.querySelector(
      'cart-drawer.drawer.active, cart-drawer.active, .cart-drawer.drawer.active, #main-cart-items'
    ));
    document.documentElement.classList.toggle('fp-cart-focus-active', open);
  };
  const fetchCart = async () => {
    const bounded = window.SafeWalkV51KachingRecovery?.fetchJson;
    if (typeof bounded === 'function') {
      const { response, body } = await bounded('/cart.js', {
        headers: { Accept: 'application/json' }, cache: 'no-store'
      }, CART_READ_TIMEOUT_MS);
      if (!response.ok) throw new Error(`cart-${response.status}`);
      return body;
    }
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), CART_READ_TIMEOUT_MS);
    try {
      const response = await window.fetch('/cart.js', {
        headers: { Accept: 'application/json' }, cache: 'no-store', signal: controller.signal
      });
      if (!response.ok) throw new Error(`cart-${response.status}`);
      return response.json();
    } finally {
      window.clearTimeout(timeout);
    }
  };
  const readCart = async () => {
    if (cachedCart && Date.now() - cachedAt < CART_CACHE_MS) return cachedCart;
    const revision = cartRevision;
    if (cartRequest?.revision === revision) return cartRequest.promise;
    const promise = fetchCart().then((cart) => {
      if (revision !== cartRevision) return null;
      cachedCart = cart;
      cachedAt = Date.now();
      return cart;
    }).finally(() => {
      if (cartRequest?.promise === promise) cartRequest = null;
    });
    cartRequest = { revision, promise };
    return promise;
  };
  const sync = async () => {
    syncFocus();
    const drawers = [...document.querySelectorAll('.cart-drawer')];
    if (!drawers.length) return;
    let cart = null;
    try { cart = await readCart(); } catch (error) { /* pickup copy remains the safe fallback */ }
    drawers.forEach((drawer) => {
      syncShipping(drawer, cart);
      if (cart) syncSavings(drawer, cart);
    });
  };
  const run = async () => {
    timer = 0;
    if (running) { rerun = true; return; }
    running = true;
    try { await sync(); } finally {
      running = false;
      if (rerun) { rerun = false; schedule(); }
    }
  };
  const schedule = (fresh = false) => {
    if (fresh === true && Date.now() >= acceptedCartUntil) {
      cartRevision += 1;
      cachedCart = null;
      cachedAt = 0;
    }
    syncFocus();
    if (running) { rerun = true; return; }
    if (timer) window.clearTimeout(timer);
    timer = window.setTimeout(run, 40);
  };
  const relevantMutation = (record) => {
    if (record.type === 'attributes') return record.target?.matches?.('cart-drawer, .cart-drawer');
    return [...record.addedNodes, ...record.removedNodes].some((node) => node.nodeType === 1 && (
      node.matches?.('cart-drawer, .cart-drawer, #main-cart-items, [data-safewalk-gift-surface]')
      || node.querySelector?.('cart-drawer, .cart-drawer, #main-cart-items, [data-safewalk-gift-surface]')
    ));
  };
  const cartChildMutation = (record) => {
    if (record.type !== 'childList') return false;
    const cartStructure = [
      '#CartDrawer-CartItems', '.drawer__cart-items-wrapper', 'ul.cart-items',
      '.cart-drawer-item', 'cart-items', '#main-cart-items', '[data-safewalk-gift-surface]'
    ].join(', ');
    const elements = [...record.addedNodes, ...record.removedNodes]
      .filter((node) => node.nodeType === 1);
    if (!elements.length) return false;
    const targetInCart = record.target?.matches?.(cartStructure)
      || record.target?.closest?.('cart-drawer, .cart-drawer, cart-items, #main-cart-items');
    return Boolean(targetInCart);
  };
  const runGiftReconciliation = async () => {
    reconciliationTimer = 0;
    const lifecycle = window.SafeWalkV8GiftPackLifecycle || window.SafeWalkV6RealGiftLifecycle;
    if (lifecycle?.isRecentlyVerified?.()) return;
    const reconcile = lifecycle?.reconcileExistingCart;
    if (typeof reconcile !== 'function') return;
    if (reconciliationRunning) { reconciliationQueued = true; return; }
    reconciliationRunning = true;
    try { await reconcile(); } finally {
      reconciliationRunning = false;
      if (reconciliationQueued) {
        reconciliationQueued = false;
        scheduleGiftReconciliation();
      }
    }
  };
  const acceptCart = (cart) => {
    if (!cart || !Array.isArray(cart.items)) return;
    cartRevision += 1;
    cachedCart = cart;
    cachedAt = Date.now();
    acceptedCartUntil = cachedAt + CART_CACHE_MS;
  };
  const scheduleGiftReconciliation = () => {
    if (window.SafeWalkV6ContractMode !== 'kaching_native_v1') return;
    if (reconciliationRunning) { reconciliationQueued = true; return; }
    if (reconciliationTimer) return;
    reconciliationTimer = window.setTimeout(runGiftReconciliation, 60);
  };

  new MutationObserver((records) => {
    const cartChanged = records.some(cartChildMutation);
    if (cartChanged) scheduleGiftReconciliation();
    if (cartChanged || records.some(relevantMutation)) schedule(true);
  }).observe(document.documentElement, {
    childList: true, subtree: true, attributes: true, attributeFilter: ['class']
  });
  ['shopify:section:load', 'shopify:section:unload', 'shopify:block:select', 'safewalk:real-gifts-rendered']
    .forEach((eventName) => document.addEventListener(eventName, () => schedule(true)));
  window.addEventListener('pageshow', () => schedule(true));
  window.SafeWalkV6CartSavingsTruth = Object.freeze({
    centsFromText,
    formattedLike,
    mainKind,
    validatedGiftLine,
    drawerAdjustment,
    isFullSet,
    syncShipping,
    syncSavings,
    syncFocus,
    acceptCart,
    sync,
    scheduleGiftReconciliation,
    schedule
  });
  schedule();
})();
